import { FileText, Hash, Mail } from "lucide-react";
import type { AgentTask } from "@/api/agent-tasks";

/**
 * How a task is described on screen, shared by the three Tasks views and the
 * task drawer so they cannot drift apart again (each used to carry its own
 * copy of these, and they disagreed: "Next: Just now" in one view, "in 16h"
 * in another, for the same task).
 */

/** "3m ago", "in 16h", "—". Counts both ways. */
export function relativeTime(dateStr: string | null | undefined): string {
  if (!dateStr) return "—";
  const diff = Date.now() - new Date(dateStr).getTime();
  const abs = Math.abs(diff);
  const amount =
    abs < 60_000
      ? null
      : abs < 3_600_000
        ? `${Math.round(abs / 60_000)}m`
        : abs < 86_400_000
          ? `${Math.round(abs / 3_600_000)}h`
          : `${Math.round(abs / 86_400_000)}d`;
  if (diff < 0) return amount ? `in ${amount}` : "in <1m";
  return amount ? `${amount} ago` : "just now";
}

/** Full date and time, for the drawer. */
export function formatDateTime(iso: string | null | undefined): string {
  if (!iso) return "—";
  return new Date(iso).toLocaleString(undefined, {
    weekday: "short",
    day: "numeric",
    month: "short",
    hour: "2-digit",
    minute: "2-digit",
  });
}

export type TaskState = "active" | "paused" | "pending" | "done" | "failed";

/**
 * One status per task. The stored `status` said "active" on a paused task and
 * on one-off tasks that had long finished, so it is derived here instead:
 * a recurring task is Active or Paused; a one-off task is Pending until it
 * runs, then Done or Failed.
 */
export function taskState(task: AgentTask): TaskState {
  if (task.type === "recurring") return task.enabled ? "active" : "paused";
  if (task.lastResult?.success === false || task.status === "failed") return "failed";
  if (task.lastRunAt || task.status === "completed") return "done";
  return "pending";
}

export const TASK_STATE_LABEL: Record<TaskState, string> = {
  active: "Active",
  paused: "Paused",
  pending: "Pending",
  done: "Done",
  failed: "Failed",
};

export const TASK_STATE_VARIANT: Record<TaskState, "success" | "warning" | "danger" | "secondary" | "default"> = {
  active: "success",
  paused: "warning",
  pending: "default",
  done: "secondary",
  failed: "danger",
};

/**
 * When the task runs next, or null. Recurring tasks only while active. The
 * list's `nextRun` is computed from the schedule; the stored `nextRunAt` can
 * be in the past, which is what made the top-bar view say "Just now".
 */
export function nextRunOf(task: AgentTask): string | null {
  if (task.type === "recurring" && !task.enabled) return null;
  const next = task.nextRun ?? task.nextRunAt;
  if (!next || new Date(next).getTime() < Date.now()) return null;
  return next;
}

/**
 * Who made the task. `createdBy` holds the person's email for tasks made on
 * screen since 2026-10-08, "agent" for tasks an agent made itself, and
 * "human" for tasks made on screen before the email was recorded.
 */
export function createdByLabel(task: AgentTask): string {
  const by = (task.createdBy || "").trim();
  if (by === "agent") return `${task.agentName ?? "The agent"} (agent)`;
  if (!by || by === "human") return "A person (name not recorded)";
  return by;
}

/** Where a task's result lands. */
export function describeDestination(task: AgentTask): {
  icon: typeof Hash;
  label: string;
  hint?: string;
} {
  if (task.announceChannelName) {
    return { icon: Hash, label: `#${task.announceChannelSlug || task.announceChannelName}`, hint: "channel" };
  }
  const prompt = (task.taskPrompt || "").toLowerCase();
  if (prompt.includes("email") || prompt.includes("mail ") || prompt.includes("send to")) {
    return { icon: Mail, label: "Via agent email", hint: "in the task prompt" };
  }
  return { icon: FileText, label: "Task Reports", hint: "no channel set" };
}
